import { Badge, Text, Link, Hidden, TextLink } from 'braid-design-system';
import type { FC } from 'react';

import translations from './.vocab';
import { useTranslations } from '@vocab/react';
import { TableRowLayout } from '../TableLayout';
import { StatusBadge } from '../StatusBadge';
import { CandidateRowOverflowMenu } from './CandidateRowOverflowMenu';
import { TimeAgo } from 'src/components/common';
import type { YourCandidatesNode_CandidateRowFragment } from '../YourCandidates/YourCandidates.generated';
import { JOB_APPLICATION_TAB } from 'src/constants/tabs';
import { MostRecentRoleMobile } from './MostRecentRoleMobile';
import type { YourCandidateStatus } from '../graphql/hirer-types.generated';
import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';

const statusQueryStringMap: Record<YourCandidateStatus, string | undefined> = {
  NEW: undefined,
  VIEWED: undefined,
  PRESCREEN: 'Prescreen',
  SHORTLIST: 'Shortlist',
  INTERVIEW: 'Interview',
  OFFER: 'Offer',
  ACCEPT: 'Accept',
  REJECTED: 'Rejected',
};

export interface CandidateRowProps {
  candidate: YourCandidatesNode_CandidateRowFragment;
  onClickCandidateName: () => void;
  onClickPreviousApplication: () => void;
  onClickCoverLetterDownload: () => void;
  onClickResumeDownload: () => void;
  onClickSelectionCriteriaDownload: () => void;
  onClickEmailCandidate: () => void;
}

const PreviousApplicationContent = ({
  previousApplicationCount,
}: {
  previousApplicationCount: number;
}) => {
  const { t } = useTranslations(translations);
  return (
    <Text weight="regular">
      <Hidden above="mobile">
        {previousApplicationCount > 0 &&
          t('Previous applications: totalApplications', {
            totalApplications: previousApplicationCount,
          })}
      </Hidden>
      <Hidden below="tablet">{previousApplicationCount}</Hidden>
    </Text>
  );
};

export const CandidateRow: FC<CandidateRowProps> = ({
  candidate: {
    name,
    phoneNumber,
    channel,
    status,
    lastRoleJobTitle,
    lastRoleCompanyName,
    jobTitle,
    totalApplications,
    hasCoverLetter,
    hasResume,
    hasSelectionCriteria,
    jobId,
    prospectId,
    applicationTime,
  },
  onClickCandidateName,
  onClickPreviousApplication,
  onClickCoverLetterDownload,
  onClickResumeDownload,
  onClickSelectionCriteriaDownload,
  onClickEmailCandidate,
}) => {
  const { t } = useTranslations(translations);
  const { router, locale } = useAppConfig();

  const statusQueryString = statusQueryStringMap[status];

  const displayedTotalApplications = totalApplications - 1;

  return (
    <TableRowLayout
      name={
        <Text weight="strong">
          <Link
            href={router.candidates({
              jobid: jobId,
              selected: prospectId.toString(),
              s: statusQueryString,
            })}
            onClick={onClickCandidateName}
          >
            {name}
          </Link>
        </Text>
      }
      manuallyAdded={
        channel === 'UPLOADED' ? (
          <Badge tone="neutral">{t('Manually added')}</Badge>
        ) : null
      }
      phoneNumber={
        phoneNumber ? (
          <Text>
            <TextLink href={`tel:${phoneNumber}`}>{phoneNumber}</TextLink>
          </Text>
        ) : null
      }
      status={<StatusBadge status={status} />}
      mostRecentRole={
        lastRoleJobTitle ? (
          <Text>{lastRoleJobTitle}</Text>
        ) : (
          <Text>{t('New to workforce')}</Text>
        )
      }
      mostRecentCompanyName={
        lastRoleJobTitle && lastRoleCompanyName ? (
          <Text>{lastRoleCompanyName}</Text>
        ) : null
      }
      mostRecentRoleMobile={
        <MostRecentRoleMobile
          mostRecentRoleCompanyName={lastRoleCompanyName}
          mostRecentRoleTitle={lastRoleJobTitle}
        />
      }
      mostRecentApplication={
        <Text>
          <Hidden above="mobile">
            {t('Applied for {jobTitle}', { jobTitle })}
          </Hidden>
          <Hidden below="tablet">{jobTitle}</Hidden>
        </Text>
      }
      relativeApplicationTime={
        <Text tone="secondary">
          <TimeAgo date={applicationTime} locale={locale} />
        </Text>
      }
      previousApplications={
        displayedTotalApplications > 0 ? (
          <TextLink
            weight="weak"
            href={router.candidates({
              jobid: jobId,
              selected: prospectId.toString(),
              tab: JOB_APPLICATION_TAB.PREVIOUS_APPLICATIONS,
              s: statusQueryString,
            })}
            onClick={onClickPreviousApplication}
          >
            <PreviousApplicationContent
              previousApplicationCount={displayedTotalApplications}
            />
          </TextLink>
        ) : (
          <PreviousApplicationContent
            previousApplicationCount={displayedTotalApplications}
          />
        )
      }
      kebab={
        <CandidateRowOverflowMenu
          hasCoverLetter={hasCoverLetter}
          hasResume={hasResume}
          hasSelectionCriteria={hasSelectionCriteria}
          jobId={jobId}
          prospectId={prospectId}
          statusQueryString={statusQueryString}
          onClickCoverLetterDownload={onClickCoverLetterDownload}
          onClickResumeDownload={onClickResumeDownload}
          onClickSelectionCriteriaDownload={onClickSelectionCriteriaDownload}
          onClickEmailCandidate={onClickEmailCandidate}
        />
      }
    />
  );
};
